import { useQueryClient } from '@tanstack/react-query';

import { EnvironmentAccessCard } from '@/components/profile/EnvironmentAccessCard';
import { ProfileForm } from '@/components/profile/ProfileForm';
import { SessionsCard } from '@/components/profile/SessionsCard';
import type { AllowedEnvironments } from '@/lib/mode/ModeProvider';
import {
  getPortalProfileGetPortalMeQueryKey,
  getPortalProfileGetPortalMeSessionsQueryKey,
  usePortalProfileGetPortalMe,
  usePortalProfileGetPortalMeSessions,
  usePortalProfilePostPortalMeChangePassword,
  usePortalProfilePostPortalMeSessionsIdGuidRevoke,
  usePortalProfilePostPortalMeSessionsRevokeOthers,
  usePortalProfilePutPortalMe,
} from '@/lib/api/portal-profile/portal-profile';
import { usePortalAuth } from '@/lib/portal-auth/usePortalAuth';

/**
 * Self-service profile page for portal (client) users. Mirror of the admin
 * <ProfilePage>; the only differences are the orval hook namespace
 * (`portalProfile*` vs `adminProfile*`) and the auth context used for the
 * post-email-change token refresh.
 */
function readApiErrorDetail(err: unknown): string | undefined {
  if (err === null || err === undefined) return undefined;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const r = err as Record<string, unknown>;
    if (typeof r.detail === 'string') return r.detail;
    if (typeof r.title === 'string') return r.title;
    if (typeof r.message === 'string') return r.message;
    const errors = r.errors;
    if (errors && typeof errors === 'object') {
      const first = Object.values(errors as Record<string, unknown>)[0];
      if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
    }
  }
  return undefined;
}

export default function PortalProfilePage() {
  const queryClient = useQueryClient();
  const auth = usePortalAuth();
  const meQuery = usePortalProfileGetPortalMe();
  const putMutation = usePortalProfilePutPortalMe();
  const pwMutation = usePortalProfilePostPortalMeChangePassword();

  if (meQuery.isLoading) {
    return <PageShell><div className="text-sm text-muted-foreground">Loading profile…</div></PageShell>;
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <PageShell>
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
          Could not load your profile. Refresh the page or sign in again.
        </div>
      </PageShell>
    );
  }

  const me = meQuery.data;
  // Portal users default to `'Production'` until Phase B ships the field —
  // sandbox is opt-in by re-invite.
  const allowed: AllowedEnvironments =
    (me as { allowedEnvironments?: AllowedEnvironments }).allowedEnvironments ?? 'Production';

  return (
    <PageShell>
      <SessionsPanel />
      <EnvironmentAccessCard allowed={allowed} contact="your ClientAdmin" />
      <ProfileForm
        initial={{ email: me.email, fullName: me.fullName, role: String(me.role) }}
        // Email change is disabled at the UI level. See ProfilePage for the
        // full rationale — self-service email rotation re-enables when a
        // verification flow ships.
        allowEmailEdit={false}
        isSavingPersonal={putMutation.isPending}
        isChangingPassword={pwMutation.isPending}
        onSavePersonal={async ({ fullName, email, kind }) => {
          await putMutation.mutateAsync({ data: { fullName, email } }).catch((err) => {
            throw new Error(readApiErrorDetail(err) ?? 'Could not save profile.');
          });
          await queryClient.invalidateQueries({ queryKey: getPortalProfileGetPortalMeQueryKey() });
          if (kind === 'email-changed') {
            // Force a portal token refresh so the email claim stays in sync.
            await (auth as { refresh?: () => Promise<unknown> }).refresh?.().catch(() => undefined);
          }
        }}
        onChangePassword={async ({ currentPassword, newPassword }) => {
          await pwMutation.mutateAsync({ data: { currentPassword, newPassword } }).catch((err) => {
            throw new Error(readApiErrorDetail(err) ?? 'Could not change password.');
          });
        }}
      />
    </PageShell>
  );
}

function SessionsPanel() {
  const queryClient = useQueryClient();
  const sessionsQuery = usePortalProfileGetPortalMeSessions();
  const revokeOne = usePortalProfilePostPortalMeSessionsIdGuidRevoke();
  const revokeOthers = usePortalProfilePostPortalMeSessionsRevokeOthers();

  const sessions = (sessionsQuery.data ?? []).map((s) => ({
    id: s.id,
    ipAddress: s.ipAddress,
    userAgent: s.userAgent,
    createdAt: s.createdAt,
    expiresAt: s.expiresAt,
    revokedAt: s.revokedAt,
    isCurrent: s.isCurrent,
  }));

  return (
    <SessionsCard
      sessions={sessions}
      isLoading={sessionsQuery.isLoading}
      onRevokeSession={async (sessionId) => {
        await revokeOne.mutateAsync({ id: sessionId });
        await queryClient.invalidateQueries({ queryKey: getPortalProfileGetPortalMeSessionsQueryKey() });
      }}
      onRevokeOthers={async () => {
        const res = await revokeOthers.mutateAsync();
        await queryClient.invalidateQueries({ queryKey: getPortalProfileGetPortalMeSessionsQueryKey() });
        // orval models int as `number | string` via OpenAPI's int64 pattern;
        // narrow to number for the SessionsCard contract.
        return { revokedCount: Number(res.revokedCount) };
      }}
    />
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Edit your name, email, or password. Sensitive changes sign other
          sessions out.
        </p>
      </header>
      {children}
    </div>
  );
}
